import { Typography, Paper } from '@mui/material'
import { Delete as DeleteIcon } from '@mui/icons-material'
import ConfirmationModal from './confirmation-modal'

type DeleteConfirmationModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  entityName: string
  entityDisplayName: string
  isSubmitting?: boolean
  additionalContent?: React.ReactNode
}

/**
 * Reusable confirmation modal for delete operations
 *
 * Provides a consistent UX for delete confirmations across all v4 modules
 *
 * @example
 * <DeleteConfirmationModal
 *   isOpen={dialogStatus === `delete-${item.id}`}
 *   onClose={() => setDialogStatus('')}
 *   onConfirm={() => handleDelete(item)}
 *   entityName="group"
 *   entityDisplayName={group.name}
 *   isSubmitting={isDeleting}
 * />
 */
export default function DeleteConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  entityName,
  entityDisplayName,
  isSubmitting = false,
  additionalContent,
}: DeleteConfirmationModalProps) {
  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Confirm Deletion"
      confirmText="Delete"
      confirmColor="error"
      confirmStartIcon={<DeleteIcon />}
      isSubmitting={isSubmitting}
    >
      <Typography className="mb-4">
        Are you sure you want to delete the following {entityName}?
      </Typography>
      <Paper className="p-4 mb-4" sx={{ bgcolor: 'background.paperDark' }}>
        <Typography variant="subtitle1" className="font-bold">
          {entityDisplayName}
        </Typography>
      </Paper>
      {additionalContent}
      <Typography variant="body2" color="text.secondary">
        This action cannot be undone.
      </Typography>
    </ConfirmationModal>
  )
}
